import { FC, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { nanoid } from 'nanoid'

import { BlackListWarning } from 'pages/avia/my-ticket/pages/ListPassengers/components/black-list-warning'
import { BlackListModal } from 'pages/avia/purchase-form/pages/confirm/components/black-list-modal'

import { DetailsAppeal } from 'shared/api/BuyTicketModel'
import { MyTicketItem } from 'shared/api/MyTicketsModel'
import { Passenger } from 'shared/api/MyTicketsModel.ts'
import { ADDITIONAL_SERVICE, TYPE_APPELS } from 'shared/constants'
import { previewPdfUrl } from 'shared/lib/pdf-preview/index.ts'
import { priceShow } from 'shared/lib/priceShow'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { Paper } from 'shared/ui/paper'

import { RenderItem } from './components/render-item'
import { getPassengerDetails } from './lib/getPassengerDetails.tsx'
import s from './styles.module.scss'

interface Props {
  ticket: MyTicketItem
  additionalService?: DetailsAppeal[]
  onDetailPassenger: (passenger: Passenger) => void
}

export const FlightPassengerInfo: FC<Props> = ({
  ticket,
  additionalService,
}) => {
  const { t } = useTranslation()
  const { openDialog } = useDialogBottomSheet()

  const [isOpen, setIsOpen] = useState(true)
  const [expandedPassengerId, setExpandedPassengerId] = useState<number | null>(
    null,
  )

  const passengersList = ticket?.flight_details?.tickets?.[0]?.passengers

  const hasBlacklisted = useMemo(() => {
    return passengersList?.some((p) => p.is_black_listed)
  }, [passengersList])

  const listAdditionalService = useMemo(
    () =>
      additionalService?.filter(
        (service) => service.topic === TYPE_APPELS.ADDITIONAL_SERVICE,
      ),
    [additionalService],
  )

  const getPassengerServices = (selectedPassenger: Passenger) =>
    listAdditionalService?.filter((service) => {
      const passengerKeys = service.flight_appeal.appeals_detail.passengers.map(
        (p) => p.passenger_key,
      )
      return passengerKeys.includes(selectedPassenger.key)
    })

  const togglePassengerExpand = (passengerId: number) => {
    setExpandedPassengerId((prev) =>
      prev === passengerId ? null : passengerId,
    )
  }

  const onBlackListClick = (passengerName: string) => {
    openDialog({
      isDialog: false,
      child: <BlackListModal passengersNames={passengerName} />,
    })
  }

  const btnHandlerOpenPDF = (link?: string) => {
    previewPdfUrl(link)
  }

  return (
    <div className={s.container}>
      <Paper
        padding={[16]}
        radius={16}
      >
        <div className={s.paperContainer}>
          <div
            className={s.wrapper}
            onClick={() => setIsOpen(!isOpen)}
          >
            <p className={clsx('semi-bold-17')}>{t('passengers')}</p>

            <div className={s.content}>
              {hasBlacklisted && (
                <BlackListWarning title={t('black_list.restriction')} />
              )}

              <div className={clsx('medium-12', s.count)}>
                <p className={clsx('medium-12', s.countText)}>
                  {passengersList.length}
                </p>
              </div>

              <AppIcon
                id={AllIcons.ARROW_RIGHT_TARIFF}
                width={20}
                height={20}
                className={clsx(s.icon, isOpen && s.icon_open)}
              />
            </div>
          </div>

          {isOpen && (
            <div>
              {passengersList?.map((item, index) => {
                const isExpanded = expandedPassengerId === item.id
                const { typeAge, listDetail } = getPassengerDetails(item)

                const passengerName = `${item?.name?.first || ''} ${item?.name?.last || ''} ${item?.name?.middle || ''}`

                return (
                  <div
                    key={'names' + index}
                    className={s.passengerItemWrapper}
                  >
                    <div
                      className={s.passengerItem}
                      onClick={() => togglePassengerExpand(item.id)}
                    >
                      <div className={s.left}>
                        <p className={s.item}>{passengerName}</p>
                      </div>

                      <div className={s.icon}>
                        <AppIcon
                          id={AllIcons.ARROW_RIGHT}
                          className={clsx(
                            s.arrow,
                            isExpanded && s.arrow_expanded,
                          )}
                        />
                      </div>
                    </div>

                    {isExpanded && (
                      <div className={s.passengerDetail}>
                        <p className={s.passengerDetailTitle}>
                          {`${typeAge?.option || ''} ${index + 1}`}
                        </p>

                        {item.is_black_listed && (
                          <div className={s.warningBlock}>
                            <BlackListWarning
                              title={t('black_list.probably_restriction')}
                            />
                            <div
                              className={clsx(s.moreLink, 'medium-12')}
                              onClick={() => onBlackListClick(passengerName)}
                            >
                              <p>{t('more')}</p>

                              <AppIcon
                                id={AllIcons.ARROW_RIGHT}
                                width={16}
                                height={16}
                              />
                            </div>
                          </div>
                        )}

                        <div className={s.wrapperDetail}>
                          {listDetail?.map((detailItem) => (
                            <RenderItem
                              key={nanoid()}
                              label={detailItem.label}
                              title={detailItem.title}
                            />
                          ))}
                        </div>

                        {getPassengerServices(item)?.map(
                          (service, serviceIndex) => {
                            const servicePassenger =
                              service.flight_appeal.appeals_detail.passengers.find(
                                (p) => p.passenger_key === item.key,
                              )

                            if (!servicePassenger) return null

                            return (
                              <div
                                className={s.serviceItem}
                                key={service.id}
                              >
                                <Paper padding={[16]}>
                                  <div className={s.information}>
                                    <h3 className={s.serviceTitle}>
                                      {`${t('additional_service_title')} №${serviceIndex + 1}`}
                                    </h3>

                                    <div className={s.status}>
                                      <p>{t('status')}</p>
                                      {servicePassenger.status ===
                                      ADDITIONAL_SERVICE.APPROVED ? (
                                        <span className={s.approved}>
                                          {t('approved')}
                                        </span>
                                      ) : null}
                                      {servicePassenger.status ===
                                      ADDITIONAL_SERVICE.CLOSED ? (
                                        <span className={s.closed}>
                                          {t('closed')}
                                        </span>
                                      ) : null}
                                      {ADDITIONAL_SERVICE.WORKING ===
                                      servicePassenger.status ? (
                                        <span className={s.wrapperStatus}>
                                          {t('status_names.pending')}
                                        </span>
                                      ) : null}
                                    </div>

                                    <div className={s.informationBlock}>
                                      <span>{t('title_service')}</span>
                                      <p>{service?.flight_appeal?.topic}</p>
                                    </div>

                                    {servicePassenger?.price_of_service ? (
                                      <div className={s.informationBlock}>
                                        <span>{t('price')}</span>
                                        <p>
                                          {priceShow(
                                            servicePassenger.price_of_service,
                                          )}{' '}
                                          &#8384;
                                        </p>
                                      </div>
                                    ) : null}
                                  </div>
                                </Paper>
                              </div>
                            )
                          },
                        )}
                      </div>
                    )}
                  </div>
                )
              })}

              <div className={s.block}>
                <div className={s.info}>
                  <span className={clsx('regular-12', s.info_label)}>
                    {t('order_number')}
                  </span>
                  <p className={clsx('regular-14', s.info_value)}>
                    {ticket.billing_number}
                  </p>
                </div>
              </div>

              {ticket?.ticket_receipt_url && (
                <div className={s.showTicket}>
                  <p
                    onClick={() =>
                      btnHandlerOpenPDF(ticket?.ticket_receipt_url)
                    }
                  >
                    <span className={clsx(s.text, 'medium-14')}>
                      {t('see_ticket')}
                    </span>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </Paper>
    </div>
  )
}
